import { DialogContentText, TextField } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import FormSpacer from "@saleor/components/FormSpacer";
import SingleSelectField from "@saleor/components/SingleSelectField";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import ProductLensType from "@saleor/productLensType/components/ProductLensType/ProductLensType";
import {
  useProductLensTypeCreateMutation,
  useProductLensTypeDeleteMutation,
  useReOrderProductLensTypeMutation,
  useUpdateProductLensTypeMutation
} from "@saleor/productLensType/mutations";
import { useProductLensTypeListQuery } from "@saleor/productLensType/queries";
import {
  ProductLensTypeListUrl,
  ProductLensTypeListUrlDialog,
  ProductLensTypeListUrlQueryParams
} from "@saleor/productLensType/urls";
import { ReorderEvent } from "@saleor/types";
import { PrescriptionStartSelectEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { move } from "@saleor/utils/lists";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface HotSearchListProps {
  params: ProductLensTypeListUrlQueryParams;
}

const ProductLensTypeList: React.FC<HotSearchListProps> = ({ params }) => {
  const notify = useNotifier();
  const navigate = useNavigator();
  const intl = useIntl();

  const { data, refetch } = useProductLensTypeListQuery({
    variables: {
      first: 100
    }
  });
  const [productLensTypeReorder] = useReOrderProductLensTypeMutation({
    onCompleted: data => {
      if (data.productLensTypeReorder.errors.length !== 0) {
        notify({
          status: "error",
          text: "error"
        });
      }
      refetch();
    }
  });
  const handleProductLensTypeReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    productLensTypeReorder({
      optimisticResponse: {
        productLensTypeReorder: {
          __typename: "ProductLensTypeReOrder",
          productLensTypeList: move(
            data.productLensTypes.edges.map(edge => edge.node)[oldIndex],
            data.productLensTypes.edges.map(edge => edge.node),
            (a, b) => a.id === b.id,
            newIndex
          ),
          errors: []
        }
      },
      variables: {
        move: {
          id: data.productLensTypes.edges.map(edge => edge.node)[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });
  const [openModal, closeModal] = createDialogActionHandlers<
    ProductLensTypeListUrlDialog,
    ProductLensTypeListUrlQueryParams
  >(navigate, params => ProductLensTypeListUrl(params), params);

  const [
    productLensTypeDelete,
    productLensTypeDeleteOpts
  ] = useProductLensTypeDeleteMutation({
    onCompleted: data => {
      if (data.productLensTypeDelete.errors.length === 0) {
        closeModal();
        refetch();
      }
    }
  });
  const [
    updateProductLensType,
    updateProductLensTypeOpts
  ] = useUpdateProductLensTypeMutation({
    onCompleted: data => {
      if (data.productLensTypeUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        closeModal();
        refetch();
      }
    }
  });

  const [
    createProductLensType,
    createProductLensTypeOpts
  ] = useProductLensTypeCreateMutation({
    onCompleted: data => {
      if (data.productLensTypeCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        closeModal();
        refetch();
      }
    }
  });

  const [name, setName] = React.useState<string>(null);
  const [extraChargeCode, setExtraChargeCode] = React.useState<string>(null);
  const [skuStartCode, setSkuStartCode] = React.useState<string>(null);
  const [prescriptionStartSelect, setPrescriptionStartSelect] = React.useState<
    string
  >(null);

  const PrescriptionStartSelectChoices = [
    {
      label: PrescriptionStartSelectEnum.YESNO,
      value: PrescriptionStartSelectEnum.YESNO
    },
    {
      label: PrescriptionStartSelectEnum.COLOUR,
      value: PrescriptionStartSelectEnum.COLOUR
    },
    {
      label: PrescriptionStartSelectEnum.COMBINATION,
      value: PrescriptionStartSelectEnum.COMBINATION
    },
    {
      label: PrescriptionStartSelectEnum.NONE,
      value: PrescriptionStartSelectEnum.NONE
    }
  ];

  return (
    <>
      <ProductLensType
        data={data?.productLensTypes?.edges?.map(edge => edge.node)}
        onReorder={handleProductLensTypeReorder}
        onDelete={id => openModal("delete", { id })}
        onAdd={() => openModal("create")}
        onUpdate={(
          id,
          _name,
          _extraChargeCode,
          _skuStartCode,
          _prescriptionStartSelect
        ) => {
          openModal("update", { id });
          setName(_name);
          setExtraChargeCode(_extraChargeCode);
          setSkuStartCode(_skuStartCode);
          setPrescriptionStartSelect(_prescriptionStartSelect);
        }}
        onVisible={(id, name, isActive) => {
          updateProductLensType({
            variables: {
              id,
              input: {
                name,
                isActive
              }
            }
          });
        }}
      />
      <ActionDialog
        open={params.action === "delete"}
        onClose={closeModal}
        confirmButtonState={productLensTypeDeleteOpts.status}
        onConfirm={() =>
          productLensTypeDelete({
            variables: {
              id: params.id
            }
          })
        }
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Lens Type",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete Lens Type?"
            description="dialog content"
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "update"}
        onClose={closeModal}
        confirmButtonState={updateProductLensTypeOpts.status}
        onConfirm={() =>
          updateProductLensType({
            variables: {
              id: params.id,
              input: {
                name,
                extraChargeCode,
                skuStartCode,
                prescriptionStartSelect: prescriptionStartSelect as PrescriptionStartSelectEnum
              }
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Update Lens Type",
          description: "dialog header"
        })}
      >
        <div style={{ overflowX: "hidden", paddingRight: "10px" }}>
          <TextField
            label={intl.formatMessage({
              defaultMessage: "Name",
              description: "Name"
            })}
            fullWidth
            name="Name"
            value={name}
            onChange={event => setName(event.target.value)}
          />
          <FormSpacer />
          <TextField
            label={intl.formatMessage({
              defaultMessage: "Extra Charge Code",
              description: "Extra Charge Code"
            })}
            fullWidth
            name="ExtraChargeCode"
            value={extraChargeCode}
            onChange={event => setExtraChargeCode(event.target.value)}
          />
          <FormSpacer />

          <SingleSelectField
            choices={PrescriptionStartSelectChoices}
            label={intl.formatMessage({
              defaultMessage: "Prescription Start Select",
              description: "Prescription Start Select choice."
            })}
            name="PrescriptionStartSelect"
            onChange={event => {
              setPrescriptionStartSelect(event.target.value);
            }}
            value={prescriptionStartSelect}
          />

          <FormSpacer />

          <TextField
            label={intl.formatMessage({
              defaultMessage: "Sku Start Code",
              description: "Sku Start Code"
            })}
            fullWidth
            name="SkuStartCode"
            value={skuStartCode}
            onChange={event => setSkuStartCode(event.target.value)}
          />
        </div>
      </ActionDialog>
      <ActionDialog
        open={params.action === "create"}
        onClose={closeModal}
        confirmButtonState={createProductLensTypeOpts.status}
        onConfirm={() =>
          createProductLensType({
            variables: {
              input: {
                name,
                extraChargeCode,
                skuStartCode,
                prescriptionStartSelect: prescriptionStartSelect as PrescriptionStartSelectEnum
              }
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Create Lens Type",
          description: "dialog header"
        })}
      >
        {" "}
        <div style={{ overflowX: "hidden", paddingRight: "10px" }}>
          <TextField
            label={intl.formatMessage({
              defaultMessage: "Name",
              description: "Name"
            })}
            fullWidth
            name="Name"
            value={name}
            onChange={event => setName(event.target.value)}
          />
          <FormSpacer />
          <TextField
            label={intl.formatMessage({
              defaultMessage: "Extra Charge Code",
              description: "Extra Charge Code"
            })}
            fullWidth
            name="ExtraChargecode"
            value={extraChargeCode}
            onChange={event => setExtraChargeCode(event.target.value)}
          />
          <FormSpacer />
          <SingleSelectField
            choices={PrescriptionStartSelectChoices}
            label={intl.formatMessage({
              defaultMessage: "Prescription Start Select",
              description: "Prescription Start Select choice."
            })}
            name="PrescriptionStartSelect"
            onChange={event => {
              setPrescriptionStartSelect(event.target.value);
            }}
            value={PrescriptionStartSelectEnum.NONE}
          />

          <FormSpacer />
          <TextField
            label={intl.formatMessage({
              defaultMessage: "Sku Start Code",
              description: "Sku Start Code"
            })}
            fullWidth
            name="SkuStartCode"
            value={skuStartCode}
            onChange={event => setSkuStartCode(event.target.value)}
          />
        </div>
      </ActionDialog>
    </>
  );
};
export default ProductLensTypeList;
